import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, ShieldCheck, Unplug } from "lucide-react";
import toast from "react-hot-toast";

import PageHeader from "../../../components/shared/PageHeader";
import { disconnectGmail, getMailboxes, startGmailConnect } from "../api/mailboxApi";
import { formatMelbourneDateTime } from "../../../utils/melbourneTime";
import { resetMailboxCanSend } from "../hooks/useMailboxCanSend";

const BRAND = "#55C5D0";

// What the backend callback reports back in ?gmail=<outcome>.
const OUTCOMES = {
  connected: { ok: true, text: "Gmail connected." },
  cancelled: { text: "Gmail was not connected: the Google screen was cancelled." },
  expired: { text: "That connect link expired. Please try again." },
  wrong_account: {
    text: "Please connect the Gmail account that matches your CRM login email.",
  },
  send_not_granted: {
    text: "Gmail was not connected: the \"Send email on your behalf\" permission was unticked.",
  },
};
const FALLBACK_OUTCOME = { text: "Gmail could not be connected. Please try again." };

export default function MailboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getMailboxes());
    } catch {
      toast.error("Could not load your mailbox connection.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Show the result of a connect attempt once, then drop it from the URL so a
  // refresh does not repeat the message.
  useEffect(() => {
    const outcome = searchParams.get("gmail");
    if (!outcome) return;
    const { ok, text } = OUTCOMES[outcome] || FALLBACK_OUTCOME;
    if (ok) toast.success(text);
    else toast.error(text);
    searchParams.delete("gmail");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const gmail = data?.mailboxes?.find((m) => m.provider === "gmail");

  const connect = async () => {
    setBusy(true);
    try {
      window.location.assign(await startGmailConnect());
    } catch {
      toast.error("Could not start the Gmail connection.");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await disconnectGmail();
      resetMailboxCanSend();
      toast.success("Gmail disconnected. The CRM can no longer send from it.");
      await load();
    } catch {
      toast.error("Could not disconnect Gmail. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl">
      <PageHeader
        title="My Mailbox"
        subtitle="Connect your own Gmail so emails you send from the CRM come from your address"
      />

      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <Mail className="h-6 w-6 shrink-0" style={{ color: BRAND }} />
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-medium text-gray-900">Gmail</h2>

            {loading ? (
              <p className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking connection…
              </p>
            ) : gmail?.connected ? (
              <div className="mt-2 space-y-1 text-sm text-gray-700">
                <p className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="h-4 w-4" /> Connected as{" "}
                  <span className="font-medium break-all">{gmail.account_email}</span>
                </p>
                {gmail.connected_at && (
                  <p className="text-gray-500">
                    Since {formatMelbourneDateTime(gmail.connected_at)}
                  </p>
                )}
              </div>
            ) : (
              <p className="mt-2 text-sm text-gray-600">Not connected.</p>
            )}

            {!loading && data && !data.gmail_available && (
              <p className="mt-3 text-sm text-amber-700">
                Gmail connection is not switched on yet.
              </p>
            )}

            {!loading && data?.gmail_available && (
              <div className="mt-4">
                {gmail?.connected ? (
                  <button
                    type="button"
                    onClick={disconnect}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />}
                    Disconnect
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={connect}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm text-white disabled:opacity-60"
                    style={{ backgroundColor: BRAND }}
                  >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    Connect Gmail
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-5 text-sm text-gray-700">
        <h3 className="flex items-center gap-2 font-medium text-gray-900">
          <ShieldCheck className="h-4 w-4" style={{ color: BRAND }} /> What connecting allows
        </h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>The CRM can send emails from your Gmail when you choose to send one.</li>
          <li>The CRM cannot read your inbox or any email already in your Gmail.</li>
          <li>Only the Gmail account matching your CRM login can be connected.</li>
          <li>
            Disconnect at any time. Access is removed at Google straight away, not
            just in the CRM.
          </li>
        </ul>
      </div>
    </div>
  );
}
